import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface UsePageLoadingProps {
  dependencies?: any[];
  onLoadComplete?: () => void;
  minLoadingDuration?: number; // Minimum time to show loading overlay
}

export const usePageLoading = ({ 
  dependencies = [], 
  onLoadComplete,
  minLoadingDuration = 800 // Default minimum loading time of 800ms
}: UsePageLoadingProps = {}) => {
  const location = useLocation();
  const [isPageReady, setIsPageReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const pathRef = useRef(location.pathname);
  const startTimeRef = useRef(Date.now());
  
  // Reset state when path changes
  useEffect(() => {
    if (pathRef.current !== location.pathname) {
      pathRef.current = location.pathname;
      setIsPageReady(false);
      setIsLoading(true);
      startTimeRef.current = Date.now();
    }
  }, [location.pathname]);

  // Complete loading when dependencies are ready
  useEffect(() => {
    if (dependencies.length > 0) {
      // Check if all dependencies are ready (not undefined, not null, and not false)
      // For arrays, we consider them ready even if empty (length === 0)
      const allReady = dependencies.every(dep => {
        if (dep === undefined || dep === null) return false;
        if (Array.isArray(dep)) return true; // Arrays are ready even if empty
        if (typeof dep === 'boolean') return dep === true;
        return true; // Any other truthy value is ready
      });
      
      if (allReady && !isPageReady) {
        const elapsedTime = Date.now() - startTimeRef.current;
        const remainingTime = Math.max(0, minLoadingDuration - elapsedTime);
        
        // Ensure minimum loading duration
        setTimeout(() => {
          setIsPageReady(true);
          setIsLoading(false);
          onLoadComplete?.();
        }, remainingTime);
      }
    } else {
      // Default timeout if no dependencies specified
      const timer = setTimeout(() => {
        if (!isPageReady) {
          setIsPageReady(true);
          setIsLoading(false);
          onLoadComplete?.();
        }
      }, minLoadingDuration);
      
      return () => clearTimeout(timer);
    }
  }, [dependencies, isPageReady, onLoadComplete, minLoadingDuration]);

  return {
    isLoading,
    isPageReady,
    setPageReady: setIsPageReady
  };
};